import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { API, useAuth } from '../App';
import { convertGoogleDriveLink } from '../utils/imageUtils';

const ProductsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || 'all';

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['all']);
  const [loading, setLoading] = useState(true);

  const setSelectedCategory = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (cat === 'all') {
      next.delete('category');
    } else {
      next.set('category', cat);
    }
    setSearchParams(next, { replace: false });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(['all', ...response.data.map(c => c.name)]);
      } catch (error) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory === 'all'
          ? `${API}/products`
          : `${API}/products?category=${encodeURIComponent(selectedCategory)}`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="products-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Login Notice for Non-Logged Users */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-luxury-gold/10 border-l-4 border-luxury-gold p-6"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-heading font-bold text-luxury-gold mb-2">
                  B2B Wholesale Pricing Available
                </h3>
                <p className="text-muted-foreground">
                  Login to view exclusive wholesale prices and place orders
                </p>
              </div>
              <button
                onClick={() => navigate('/auth')}
                className="bg-luxury-gold hover:bg-luxury-darkGold text-white px-8 py-3 font-semibold transition-all duration-300 whitespace-nowrap"
              >
                Login / Register
              </button>
            </div>
          </motion.div>
        )}

        {/* Pending Approval Notice */}
        {user && user.status === 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-yellow-50 border-l-4 border-yellow-500 p-6"
          >
            <h3 className="text-lg font-heading font-bold text-yellow-800 mb-2">
              ⏳ Account Pending Approval
            </h3>
            <p className="text-yellow-700">
              Your B2B account is under review. You&apos;ll be able to view prices and place orders once approved by our team.
            </p>
          </motion.div>
        )}

        {/* Rejected Notice */}
        {user && user.status === 'rejected' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-red-50 border-l-4 border-red-500 p-6"
          >
            <h3 className="text-lg font-heading font-bold text-red-800 mb-2">
              ❌ Account Not Approved
            </h3>
            <p className="text-red-700">
              Your account application was not approved. Please contact support for more information.
            </p>
          </motion.div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">Our Collections</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Browse through our exquisite collection of traditional lehengas
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12" data-testid="category-filter">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-primary text-primary-foreground'
                  : 'border-2 border-border hover:border-primary text-foreground'
              }`}
              data-testid={`category-${category.toLowerCase()}`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground">No products found in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12" data-testid="products-grid">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`product-card-${index}`}
              >
                <Link to={`/products/${product.id}`}>
                  <div className="group relative overflow-hidden bg-transparent border-none transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="aspect-[3/4] overflow-hidden bg-accent">
                      <img
                        src={convertGoogleDriveLink(product.main_image)}
                        alt={product.name}
                        className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                        style={{
                          objectFit: product.image_fit || 'cover',
                          objectPosition: `center ${product.image_position || 'center'}`,
                          transform: `scale(${Math.min(2, Math.max(0.5, product.image_zoom || 1))})`,
                        }}
                      />
                    </div>
                    <div className="mt-4">
                      <h3 className="text-xl font-heading font-semibold mb-2">{product.name}</h3>
                      <p className="text-muted-foreground mb-2 text-sm uppercase tracking-wide">{product.category}</p>
                      {user && user.status === 'approved' ? (
                        <>
                          <p className="text-lg font-semibold text-luxury-accentGold">&#8377;{product.base_price.toFixed(2)}</p>
                          {product.variants && product.variants.length > 0 && (
                            <p className="text-sm text-muted-foreground mt-1">{product.variants.length} variants available</p>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center space-x-2 text-luxury-gold">
                          <Lock size={16} />
                          <span className="text-sm font-semibold">
                            {user && user.status === 'pending' ? 'Approval pending' : 'Login to view price'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
